/*
 * Module for response translation modal
 */

// noinspection JSUnusedLocalSymbols
define([
  "marionette",
  "i18n/i18n",
  "backbone",
  "messaging/messenger",
  "modules/topicsSplit/topic/responseTranslationModal/responseTranslationModel",
  "modules/topicsSplit/topic/responseTranslationModal/responseTranslationModalView",
  "modules/topicsSplit/topic/topicResponse/topicResponseModel",
  "modules/shared/ckeditor5/ckEditor5Module",
], function (
  Marionnete,
  i18next,
  Backbone,
  Messenger,
  ResponseTranslationModel,
  ResponseTranslationModalView,
  TopicResponseModel,
  Es6CkEditor5Module,
) {
  const CkEditor5Module = Es6CkEditor5Module.default || Es6CkEditor5Module;

  return Marionnete.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "responseTranslationModal";
      this._setUpVents();
    },
    show: function (conversationObject) {
      this.conversationNode = conversationObject.node;
      const selectedTouchPoint = conversationObject.selectedTouchPoint;
      const sourceLanguageId = conversationObject.selectedLanguage;
      const languages = Messenger.reqres.request("kb:languages");
      const sourceLanguageName = languages.get(sourceLanguageId).get("name");
      const sourceResponse = this.conversationNode.findResponseTemplate(
        selectedTouchPoint,
        sourceLanguageId,
      );
      if (!sourceResponse || sourceResponse.isResponseTextEmpty()) {
        // NOTE: This should never happen, because the button to show this dialog should be
        // hidden or disabled if there is no response
        Messenger.okDialog(i18next.t("topic.responseTranslation.noTranslateSourceResponse"), {
          ok: () => this._cancel(),
        });
        return;
      }
      const model = new Backbone.Model({
        sourceResponse: sourceResponse.get("response"),
        targetResponse: "",
        touchPointId: selectedTouchPoint,
        sourceLanguageId: sourceLanguageId,
        targetLanguageId: 0,
        sourceLanguageName: sourceLanguageName,
        sourceResponseTemplateId: sourceResponse.id,
      });

      model.languages = this._getLanguagesWithoutResponse(languages, selectedTouchPoint);
      if (model.languages.length === 0) {
        Messenger.okDialog(i18next.t("topic.responseTranslation.noTranslateTargetLanguage"), {
          ok: () => this._cancel(),
        });
        return;
      }

      Messenger.reqres.request("kb:privileged:area:roles").onSynced(() => {
        const responseWithNames = this.conversationNode.addPrivilegedRoleNamesToResponse(
          model.get("sourceResponse"),
        );
        model.set("sourceResponse", responseWithNames);
      });

      this.view = new ResponseTranslationModalView.responseTranslationModalView({
        model,
      });
      this.view.on("done", this._saveResponse, this);
      this.view.on("cancel", this._cancel, this);
      this.view.on("translateResponse", this._translateResponse, this);

      this.showRegion(this.view);
    },
    _setUpVents: function () {
      Messenger.vent.on(Messenger.getFullName("ckeditor:initializeError", this.name), () =>
        Messenger.vent.trigger("close:modal"),
      );
    },
    _getLanguagesWithoutResponse: function (allLanguages, touchPointId) {
      const filteredLanguages = new Backbone.Collection();
      allLanguages.forEach((language) => {
        const responseTemplate = this.conversationNode.findResponseTemplate(
          touchPointId,
          language.id,
        );
        if (!responseTemplate || responseTemplate.isResponseTextEmpty()) {
          filteredLanguages.add(language);
        }
      });
      return filteredLanguages;
    },
    _saveResponse: async function () {
      let allowClose = true;
      let savingDisplayShown = false;

      try {
        // disable done button to prevent user from submitting the same request twice
        this.view.disableDoneButton();
        const translatedResponse = this.htmlEditor.getResponse();
        const model = this.view.model;
        if (!TopicResponseModel.responseTemplate.EMPTY_RESPONSE_TEXT_RE.test(translatedResponse)) {
          const targetLanguageId = model.get("targetLanguageId");
          const touchPointId = model.get("touchPointId");
          const responseTemplate = this.conversationNode.setResponse(
            touchPointId,
            targetLanguageId,
            translatedResponse,
          );
          if (responseTemplate) {
            Messenger.vent.trigger("show:saving:display");
            savingDisplayShown = true;
            const kbId = Messenger.reqres.request("active:kb");
            const intentId = Messenger.reqres.request("current:topic:id");
            const isContentEmbeddedValid = await this.conversationNode.validateEmbeddedContent(
              touchPointId,
              targetLanguageId,
              translatedResponse,
              kbId,
              intentId,
            );
            if (!isContentEmbeddedValid.get("isSafe")) {
              allowClose = false;
              // noinspection ExceptionCaughtLocallyJS
              throw new Error(isContentEmbeddedValid.reason);
            }
            // update the response
            await responseTemplate.saveModelPromise();
          }
        }
      } catch (error) {
        console.error("Could not update response template", error);
        Messenger.okDialog(error);
      } finally {
        if (savingDisplayShown) {
          Messenger.vent.trigger("close:saving:display");
        }
        // Fire event that the node was selected in order to rerender the response preview
        if (allowClose) {
          Messenger.vent.trigger("selected:node", this.conversationNode);
          Messenger.vent.trigger("close:modal");
        } else {
          this.view.enableDoneButton();
        }
      }
    },
    _updateEditorWithTranslatedResponse: function (
      translatedResponse,
      targetLanguageId,
      touchPointId,
    ) {
      this.closeSubModules();
      this._showHtmlEditor(
        targetLanguageId,
        touchPointId,
        this.conversationNode.get("responseId"),
        translatedResponse,
      );
    },
    _translateResponse: async function (selectedLanguageId) {
      this.view.disableDoneButton();
      this.view.disableCancelButton();
      this.view.disableTranslateButton();
      Messenger.vent.trigger("show:saving:display");
      const response = this.view.model;

      const responseText = response.get("sourceResponse");

      try {
        const responseTranslationModel = new ResponseTranslationModel.responseTranslation({
          responseText: responseText,
          sourceLocaleId: response.get("sourceLanguageId"),
          targetLocaleId: selectedLanguageId,
        });

        await responseTranslationModel.saveModelPromise();

        const translatedText = responseTranslationModel.get("responseText");
        response.set("targetLanguageId", selectedLanguageId);
        this._updateEditorWithTranslatedResponse(
          translatedText,
          selectedLanguageId,
          response.get("touchPointId"),
        );
        this.view.enableDoneButton();
      } catch (e) {
        console.error("Failed to translate response", e);
        Messenger.okDialog(e);
      } finally {
        Messenger.vent.trigger("close:saving:display");
        this.view.enableCancelButton();
        this.view.enableTranslateButton();
      }
    },
    _cancel: function () {
      Messenger.vent.trigger("close:modal");
    },
    _showHtmlEditor: function (languageId, touchPointId, responseId, response) {
      this.htmlEditor = this.addSubModule(
        new CkEditor5Module.ckEditor5Module({ namespace: this.name, region: this.view.htmlEditor }),
      );

      this.htmlEditor.show({
        languageId: languageId,
        touchPointId: touchPointId,
        responseId: responseId,
        response: response,
        allCategories: Messenger.reqres.request("kb:categories"),
        attributes: this.conversationNode.topicAttributes(),
        categories: this.conversationNode.categories(),
        skipCollaborationFeatures: true,
        skipOpenSnippetEditor: true,
      });
    },
  });
});
